import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';
import { Link } from 'react-router-dom';

export default function QaScorecards() {
  const [range, setRange, apiParams] = useRangeState('last_30');
  const [tab, setTab] = useState('agents');
  const [filters, setFilters] = useState({});
  // 2026-07-15: coached filter. '' = all, 'yes' = coached only, 'no' = not coached only.
  const [coached, setCoached] = useState('');
  const coachedParam = coached === 'yes' ? { coached: true } : coached === 'no' ? { coached: false } : {};
  const merged = { ...apiParams, ...filters, ...coachedParam };

  const agentsQ = useQuery({
    queryKey: ['reports', 'qa', 'agents', merged],
    queryFn: () => reportsApi.qaAgents(merged),
    enabled: tab === 'agents',
    keepPreviousData: true,
  });
  const formsQ = useQuery({
    queryKey: ['reports', 'qa', 'forms', merged],
    queryFn: () => reportsApi.qaForms(merged),
    enabled: tab === 'forms',
    keepPreviousData: true,
  });
  const evalsQ = useQuery({
    queryKey: ['reports', 'qa', 'evaluators', merged],
    queryFn: () => reportsApi.qaEvaluators(merged),
    enabled: tab === 'evaluators',
    keepPreviousData: true,
  });

  const activeQ = tab === 'agents' ? agentsQ : tab === 'forms' ? formsQ : evalsQ;

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters}
        show={tab === 'agents' ? ['agent','form_id','evaluator']
              : tab === 'forms' ? ['form_id']
              : ['form_id']} />

      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-base font-semibold">QA scorecards</h2>
          <div className="text-xs">
            <Link className="text-continuum-600 hover:underline" to="/qa/my-assignments">→ My assignments</Link>
            <span className="mx-2 text-surface-muted">·</span>
            <Link className="text-continuum-600 hover:underline" to="/admin/qa-assign">Assign new evaluations</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{activeQ.data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons
            report={tab === 'agents' ? 'qa/agents' : tab === 'forms' ? 'qa/forms' : 'qa/evaluators'}
            params={merged}
            disabled={activeQ.isLoading}
          />
          <button onClick={() => activeQ.refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {activeQ.isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-surface-border dark:border-surface-dark-border mb-3">
        {[['agents','Per agent'],['forms','Per form'],['evaluators','Per evaluator']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={'px-3 py-1.5 text-sm border-b-2 -mb-px ' +
              (tab === id ? 'border-continuum-500 text-continuum-700 dark:text-continuum-200 font-medium'
                          : 'border-transparent hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')}>
            {label}
          </button>
        ))}
      </div>

      {activeQ.isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {activeQ.error?.detail || activeQ.error?.message || 'Failed to load.'}
        </div>
      )}

      {(tab === 'agents' || tab === 'evaluators') && (
        <div className="flex items-center gap-2 mb-3 text-xs">
          <span className="text-surface-muted">Coached:</span>
          {['','yes','no'].map((v) => (
            <button key={v || 'all'}
              onClick={() => setCoached(v)}
              className={'px-2 py-1 rounded-thin border ' +
                (coached === v
                  ? 'bg-continuum-500 text-white border-continuum-500'
                  : 'border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')}>
              {v === '' ? 'All' : v === 'yes' ? '🎓 Coached' : 'Not coached'}
            </button>
          ))}
        </div>
      )}

      {tab === 'agents' && (
        <ReportTable
          headers={['name','email','extension','evaluations','coached_count','avg_score','min_score','max_score','first_at','last_at']}
          rows={agentsQ.data?.rows || []}
          columnLabels={{
            name: 'Agent', email: 'Email', extension: 'Ext',
            evaluations: 'Evals',
            coached_count: '🎓 Coached',
            avg_score: 'Avg', min_score: 'Min', max_score: 'Max',
            first_at: 'First eval', last_at: 'Last eval',
          }}
        />
      )}

      {tab === 'forms' && (
        <>
          <h3 className="text-sm font-semibold mt-3 mb-2">Forms — usage & avg score</h3>
          <ReportTable
            headers={['form_name','evaluations','avg_score','min_score','max_score']}
            rows={formsQ.data?.forms || []}
            columnLabels={{
              form_name: 'Form', evaluations: 'Evals',
              avg_score: 'Avg', min_score: 'Min', max_score: 'Max',
            }}
          />
          <h3 className="text-sm font-semibold mt-4 mb-2">Questions — avg score</h3>
          <ReportTable
            headers={['form_name','question','question_type','answers','avg_score']}
            rows={formsQ.data?.questions || []}
            columnLabels={{
              form_name: 'Form', question: 'Question',
              question_type: 'Type', answers: 'Answers', avg_score: 'Avg',
            }}
          />
        </>
      )}

      {tab === 'evaluators' && (
        <ReportTable
          headers={['name','email','evaluations','coached_count','avg_score_given','score_stddev','first_at','last_at']}
          rows={evalsQ.data?.rows || []}
          columnLabels={{
            name: 'Evaluator', email: 'Email',
            evaluations: 'Evals',
            coached_count: '🎓 Coached',
            avg_score_given: 'Avg given',
            score_stddev: 'σ (variance)',
            first_at: 'First eval', last_at: 'Last eval',
          }}
        />
      )}
    </div>
  );
}
